import { useId } from "react";
import { Image as ImageIcon, Camera, Trash2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

/**
 * Photo picker — rewritten the simple, universal way.
 *
 * Why this shape:
 *   The previous version wrapped a hidden <input type="file"> in a shadcn
 *   <Button> and triggered `inputRef.current.click()` programmatically.
 *   That works on desktop and iOS, but Samsung's Chrome Android requires
 *   file-input dialogs to be opened by a *direct* user gesture; a nested
 *   `.click()` call from an onClick handler is often rejected, so the
 *   camera UI never fires an actual change event → "I take a photo, tap
 *   OK, and nothing happens".
 *
 *   The fix is pure HTML: a <label> whose `htmlFor` matches the input's
 *   `id`. Tapping the label is treated by every browser as tapping the
 *   input itself — the strongest possible user gesture — and the camera
 *   round-trip then dispatches `onChange` reliably. No refs, no JS click.
 *
 * Also exposes an optional "Supprimer" (delete) button when `onDelete`
 * is provided, so the manager can wipe the current photo without picking
 * a replacement.
 */
export default function PhotoPickerButtons({
  onChange,
  onDelete,
  testId = "photo-picker",
}) {
  const { t } = useI18n();
  const base = useId().replace(/[^a-z0-9]/gi, "");
  const fileId = `pp-file-${base}`;
  const camId = `pp-cam-${base}`;

  return (
    <div className="flex gap-2 flex-wrap">
      {/* Hidden inputs — the labels below are what the user actually taps. */}
      <input
        id={fileId}
        type="file"
        accept="image/*"
        onChange={onChange}
        className="hidden"
        data-testid={`${testId}-file-input`}
      />
      <input
        id={camId}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onChange}
        className="hidden"
        data-testid={`${testId}-camera-input`}
      />

      <label
        htmlFor={fileId}
        className="flex-1 inline-flex items-center justify-center h-14 px-4 rounded-md border border-input bg-background text-base font-medium hover:bg-accent cursor-pointer select-none"
        data-testid={`${testId}-file-btn`}
      >
        <ImageIcon size={18} className="mr-2" />
        {t("photoPicker.file")}
      </label>

      <label
        htmlFor={camId}
        className="flex-1 inline-flex items-center justify-center h-14 px-4 rounded-md border border-blue-500/50 text-blue-400 bg-background text-base font-medium hover:bg-blue-500/20 cursor-pointer select-none"
        data-testid={`${testId}-camera-btn`}
      >
        <Camera size={18} className="mr-2" />
        {t("photoPicker.camera")}
      </label>

      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center justify-center h-14 px-4 rounded-md border border-red-500/50 text-red-400 bg-background text-base font-medium hover:bg-red-500/20 cursor-pointer select-none"
          data-testid={`${testId}-delete-btn`}
          aria-label={t("photoPicker.delete")}
        >
          <Trash2 size={18} className="mr-2" />
          {t("photoPicker.delete")}
        </button>
      )}
    </div>
  );
}
